// src/modules/assetManagement/management/ManageAssets.tsx
// Search assets by linked Event, Product or tag, select one or more, then archive, share, unlink or delete.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  Archive, ArchiveRestore, CalendarDays, Eye, FolderSearch, Headphones, Link2Off, Package, Share2, Tag, Trash2, Upload, Wrench, Zap,
} from "lucide-react";
import { assetApi, MOCK_RESTORE_MS } from "../assetApi";
import AssetThumb from "../components/AssetThumb";
import { ConfirmModal, ShareModal, SupportModal, UnlinkModal } from "../components/AssetModals";
import MultiSelectFilter from "../components/MultiSelectFilter";
import { Button, EmptyState, SearchInput, StatusBadge, StorageBadge, useToast } from "../components/ui";
import type { Asset, AssetFilters } from "../types";
import { useAssetFilterOptions } from "../useAssetFilterOptions";
import { useMockQuery } from "../useMockQuery";
import { ENTITY, formatBytes, formatDate, ROUTES } from "../utils";

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: "attention", label: "Needs attention", meta: "Failed or rejected" },
  { value: "completed", label: "Ready" },
  { value: "queued", label: "Queued" },
  { value: "transcoding", label: "Processing" },
  { value: "failed", label: "Failed" },
  { value: "rejected", label: "Rejected" },
];
const STORAGE_OPTIONS = [
  { value: "standard", label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "restoring", label: "Restoring" },
];

type Dialog =
  | { type: "archive" | "restore" | "delete" | "share" | "unlink" }
  | { type: "support"; asset: Asset }
  | null;

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

const ManageAssets: React.FC<{ onUpload: () => void }> = ({ onUpload }) => {
  // Filters live in the URL so returning from an asset restores the same list
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const search = params.get("q") ?? "";
  const eventIds = params.getAll("event");
  const productIds = params.getAll("product");
  const tagValues = params.getAll("tag");
  const statuses = params.getAll("status") as NonNullable<AssetFilters["statuses"]>;
  const storages = params.getAll("storage") as NonNullable<AssetFilters["storages"]>;
  const page = Math.max(1, Number(params.get("page")) || 1);

  /** Each filter is a repeated URL param; null or [] clears it */
  const setFilters = (updates: Record<string, string | string[] | null>, resetPage = true) => {
    const next = new URLSearchParams(params);
    Object.entries(updates).forEach(([k, v]) => {
      next.delete(k);
      (Array.isArray(v) ? v : v ? [v] : []).forEach((x) => next.append(k, x));
    });
    if (resetPage) next.delete("page");
    setParams(next, { replace: true });
  };
  const setSearch = (v: string) => setFilters({ q: v });
  const setPage = (fn: (p: number) => number) => setFilters({ page: String(fn(page)) }, false);
  const NO_FILTERS = { q: null, event: null, product: null, tag: null, status: null, storage: null };
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<Dialog>(null);
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const replaceInput = useRef<HTMLInputElement>(null);
  const { toastNode, showToast } = useToast();

  const filters: AssetFilters = { search, eventIds, productIds, tags: tagValues, statuses, storages };
  const filterKey = JSON.stringify(filters);
  const { data: assets = [], initialLoading } = useMockQuery(() => assetApi.listAssets(filters), [filterKey]);
  const { data: allAssets = [] } = useMockQuery(() => assetApi.listAssets(), []);
  const { events, products, eventOptions, productOptions, tagOptions, productNote } = useAssetFilterOptions(eventIds, productIds);

  // ?focus=<id> from the Library: show and select that asset
  useEffect(() => {
    const focus = params.get("focus");
    if (!focus || allAssets.length === 0) return;
    const a = allAssets.find((x) => x.id === focus);
    if (a) setSelected(new Set([a.id]));
    const next = new URLSearchParams(params);
    ["q", "event", "product", "tag", "status", "storage", "page", "focus"].forEach((k) => next.delete(k));
    if (a) next.set("q", a.filename);
    setParams(next, { replace: true });
  }, [params, allAssets, setParams]);
  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(allAssets.map((a) => a.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [allAssets]);

  const pages = Math.max(1, Math.ceil(assets.length / PAGE_SIZE));
  const pageAssets = assets.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selectedAssets = useMemo(() => allAssets.filter((a) => selected.has(a.id)), [allAssets, selected]);
  const allOnPage = pageAssets.length > 0 && pageAssets.every((a) => selected.has(a.id));

  const counts = {
    total: allAssets.length,
    attention: allAssets.filter((a) => a.status === "failed" || a.status === "rejected").length,
    archived: allAssets.filter((a) => a.storage !== "standard").length,
    processing: allAssets.filter((a) => a.status === "transcoding").length,
  };

  const archivable = selectedAssets.filter((a) => a.storage === "standard");
  const restorable = selectedAssets.filter((a) => a.storage === "archived");
  const shareable = selectedAssets.filter((a) => a.storage === "standard" && a.status === "completed");
  const linked = selectedAssets.filter((a) => a.eventIds.length + a.productIds.length > 0);
  const linkTotal = selectedAssets.reduce((n, a) => n + a.eventIds.length + a.productIds.length, 0);

  const hasFilters =
    !!search || eventIds.length + productIds.length + tagValues.length + statuses.length + storages.length > 0;
  /** A quick chip is active when it is the only filter applied */
  const onlyFilter = (key: "status" | "storage", value: string) =>
    !search && eventIds.length + productIds.length + tagValues.length === 0 &&
    (key === "status" ? statuses.length === 1 && statuses[0] === value && storages.length === 0
                      : storages.length === 1 && storages[0] === value && statuses.length === 0);
  const clearFilters = () => setFilters(NO_FILTERS);
  /** Trail for the asset page so its breadcrumb returns to this exact list */
  const trail = [{ label: "Asset Management", to: `${location.pathname}${location.search}` }];

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const finish = (message: string) => {
    setDialog(null);
    setSelected(new Set());
    showToast(message);
  };

  const eventName = (id: string) => events.find((e) => e.id === id)?.title ?? id;
  const productName = (id: string) => products.find((p) => p.id === id)?.title ?? id;

  return (
    <div className="space-y-4">
      {toastNode}

      {/* Quick counts double as filters */}
      <div className="flex flex-wrap gap-2 text-sm">
        {[
          { label: "All assets", value: counts.total, active: !hasFilters, onClick: clearFilters },
          { label: "Needs attention", value: counts.attention, active: onlyFilter("status", "attention"), tone: "text-red-600", onClick: () => setFilters({ ...NO_FILTERS, status: "attention" }) },
          { label: "Processing", value: counts.processing, active: onlyFilter("status", "transcoding"), onClick: () => setFilters({ ...NO_FILTERS, status: "transcoding" }) },
          { label: "Archived", value: counts.archived, active: onlyFilter("storage", "archived"), onClick: () => setFilters({ ...NO_FILTERS, storage: "archived" }) },
        ].map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={c.onClick}
            className={`px-3 py-1.5 rounded-lg border transition ${
              c.active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
          >
            {c.label} <span className={`font-semibold ${c.active ? "" : c.tone ?? "text-slate-900"}`}>{c.value}</span>
          </button>
        ))}
      </div>

      {/* Search + filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-wrap items-center gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search assets" className="w-60" />
        <MultiSelectFilter noun="events" icon={<CalendarDays size={15} />} options={eventOptions} selected={eventIds}
          onChange={(v) => setFilters({ event: v })} />
        <MultiSelectFilter noun="products" icon={<Package size={15} />} options={productOptions} selected={productIds}
          onChange={(v) => setFilters({ product: v })} note={productNote} />
        <MultiSelectFilter noun="tags" icon={<Tag size={15} />} options={tagOptions} selected={tagValues}
          onChange={(v) => setFilters({ tag: v })} className="w-40" />
        <MultiSelectFilter noun="statuses" icon={<Zap size={15} />} options={STATUS_OPTIONS} selected={statuses}
          onChange={(v) => setFilters({ status: v })} className="w-44" />
        <MultiSelectFilter noun="storage types" icon={<Archive size={15} />} options={STORAGE_OPTIONS} selected={storages}
          onChange={(v) => setFilters({ storage: v })} className="w-44" />
        {hasFilters && (
          <button type="button" onClick={clearFilters} className="text-sm text-slate-500 hover:text-slate-800 hover:underline">
            Clear filters
          </button>
        )}
      </div>

      {/* Bulk actions: only possible here */}
      <div
        className={`sticky top-2 z-20 rounded-xl border px-4 py-2.5 flex flex-wrap items-center gap-2 transition ${
          selected.size ? "bg-slate-900 border-slate-900 text-white shadow-lg" : "bg-white border-slate-200 text-slate-500"
        }`}
      >
        <span className="text-sm font-medium mr-2">
          {selected.size ? `${selected.size} selected` : "Select one or more assets to act on them"}
        </span>
        {selected.size > 0 && (
          <>
            <Button size="sm" icon={<Archive size={14} />} disabled={!archivable.length} onClick={() => setDialog({ type: "archive" })}
              title={archivable.length ? "" : "Already archived"}>
              Archive{archivable.length && archivable.length !== selected.size ? ` (${archivable.length})` : ""}
            </Button>
            {restorable.length > 0 && (
              <Button size="sm" icon={<ArchiveRestore size={14} />} onClick={() => setDialog({ type: "restore" })}>
                Restore{restorable.length !== selected.size ? ` (${restorable.length})` : ""}
              </Button>
            )}
            <Button size="sm" icon={<Share2 size={14} />} disabled={!shareable.length} onClick={() => setDialog({ type: "share" })}
              title={shareable.length ? "" : "Only active, ready assets can be shared"}>
              Share{shareable.length && shareable.length !== selected.size ? ` (${shareable.length})` : ""}
            </Button>
            <Button size="sm" icon={<Link2Off size={14} />} disabled={!linked.length} onClick={() => setDialog({ type: "unlink" })}
              title={linked.length ? "" : "Not linked to any Event or Product"}>
              Unlink
            </Button>
            <Button size="sm" icon={<Trash2 size={14} />} className="!text-red-700" onClick={() => setDialog({ type: "delete" })}>
              Delete
            </Button>
            {selected.size === 1 && (
              <Button size="sm" icon={<Headphones size={14} />} onClick={() => setDialog({ type: "support", asset: selectedAssets[0] })}>
                Get support
              </Button>
            )}
            <button type="button" onClick={() => setSelected(new Set())} className="ml-auto text-sm text-white/70 hover:text-white">
              Clear selection
            </button>
          </>
        )}
      </div>

      {/* Table */}
      {initialLoading ? (
        <div className="h-96 rounded-xl bg-slate-200/60 animate-pulse" />
      ) : assets.length === 0 ? (
        <EmptyState
          icon={<FolderSearch size={22} />}
          title={hasFilters ? "No assets match these filters" : "No assets yet"}
          hint={hasFilters ? "Try a different event, product or tag." : "Upload your first files to get started."}
          action={hasFilters ? <Button onClick={clearFilters}>Clear filters</Button> : <Button variant="primary" icon={<Upload size={15} />} onClick={onUpload}>Upload assets</Button>}
        />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input type="checkbox" aria-label="Select all on this page" className="h-4 w-4 accent-slate-700" checked={allOnPage}
                    onChange={() =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        pageAssets.forEach((a) => (allOnPage ? next.delete(a.id) : next.add(a.id)));
                        return next;
                      })
                    } />
                </th>
                <th className="text-left px-2 py-3 font-semibold">Asset</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Linked to</th>
                <th className="text-left px-4 py-3 font-semibold">Tags</th>
                <th className="text-left px-4 py-3 font-semibold">Uploaded</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageAssets.map((a) => {
                const needsFix = a.status === "failed" || a.status === "rejected";
                return (
                  <tr key={a.id} className={selected.has(a.id) ? "bg-slate-50" : "hover:bg-slate-50/60"}>
                    <td className="px-4 py-3">
                      <input type="checkbox" className="h-4 w-4 accent-slate-700" checked={selected.has(a.id)} onChange={() => toggle(a.id)} aria-label={`Select ${a.filename}`} />
                    </td>
                    <td className="px-2 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-14 h-10 rounded-md overflow-hidden bg-slate-100 shrink-0 cursor-pointer" onClick={() => toggle(a.id)}>
                          <AssetThumb asset={a} size="sm" />
                        </span>
                        <span className="min-w-0">
                          <Link to={ROUTES.managedAsset(a.id)} state={{ trail }} className="block font-medium text-slate-800 truncate max-w-[280px] hover:underline" title={a.filename}>{a.filename}</Link>
                          <span className="block text-xs text-slate-500">{formatBytes(a.sizeBytes)} · {a.kind}</span>
                          {needsFix && a.errorMessage && <span className="block text-xs text-red-600 truncate max-w-[280px]" title={a.errorMessage}>{a.errorMessage}</span>}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <StatusBadge status={a.status} />
                        <StorageBadge storage={a.storage} hideActive />
                      </div>
                      {a.storage === "restoring" && a.restoreReadyAt && (
                        <p className="text-[11px] text-amber-700 mt-1">Ready ~{new Date(a.restoreReadyAt).toLocaleTimeString()}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[260px]">
                        {a.eventIds.slice(0, 2).map((id) => (
                          <span key={id} className={`px-1.5 py-0.5 rounded border text-[11px] font-medium truncate max-w-[120px] ${ENTITY.event.chip}`} title={eventName(id)}>{eventName(id)}</span>
                        ))}
                        {a.productIds.slice(0, 2).map((id) => (
                          <span key={id} className={`px-1.5 py-0.5 rounded border text-[11px] font-medium truncate max-w-[120px] ${ENTITY.product.chip}`} title={productName(id)}>{productName(id)}</span>
                        ))}
                        {a.eventIds.length + a.productIds.length > 4 && (
                          <span className="text-[11px] text-slate-500 self-center">+{a.eventIds.length + a.productIds.length - 4}</span>
                        )}
                        {a.eventIds.length + a.productIds.length === 0 && <span className="text-xs text-slate-400">Not linked</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[180px]">
                      <span className="line-clamp-2">{a.tags.map((t) => `#${t}`).join(" ")}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {formatDate(a.uploadedAt)}
                      <span className="block text-xs text-slate-400 truncate max-w-[150px]">{a.uploadedBy}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end items-center gap-1">
                        {needsFix && (
                          <Button size="sm" icon={<Wrench size={13} />} onClick={() => { setReplacingId(a.id); replaceInput.current?.click(); }}>
                            Replace file
                          </Button>
                        )}
                        <Link to={ROUTES.managedAsset(a.id)} state={{ trail }} title="Open asset" aria-label={`Open ${a.filename}`} className="p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100">
                          <Eye size={16} />
                        </Link>
                        <button type="button" title="Get first-line support" aria-label={`Get support for ${a.filename}`}
                          onClick={() => setDialog({ type: "support", asset: a })}
                          className="p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100">
                          <Headphones size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
              <span>
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, assets.length)} of {assets.length}
              </span>
              <div className="flex gap-2">
                <Button size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <input
        ref={replaceInput}
        type="file"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file || !replacingId) return;
          await assetApi.replaceAssetFile(replacingId, file);
          setReplacingId(null);
          showToast(`Replacing with ${file.name}; it will be reprocessed`);
        }}
      />

      {/* Dialogs */}
      {dialog?.type === "archive" && (
        <ConfirmModal
          title={`Archive ${plural(archivable.length, "asset")}?`}
          confirmLabel="Archive"
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await assetApi.archiveAssets(archivable.map((a) => a.id));
            finish(`Archived ${plural(archivable.length, "asset")}`);
          }}
          body={
            <>
              <p>Archived files move to cold storage. They keep their links, but can't be played, previewed or shared until restored.</p>
              <p className="text-slate-500">Restoring takes a few hours on the live platform (about {MOCK_RESTORE_MS / 1000} seconds in this demo).</p>
              {archivable.length < selected.size && (
                <p className="text-slate-500">{plural(selected.size - archivable.length, "selected asset")} already archived will be skipped.</p>
              )}
            </>
          }
        />
      )}
      {dialog?.type === "restore" && (
        <ConfirmModal
          title={`Restore ${plural(restorable.length, "asset")}?`}
          confirmLabel="Start restore"
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await assetApi.restoreAssets(restorable.map((a) => a.id));
            finish(`Restore started for ${plural(restorable.length, "asset")}`);
          }}
          body={<p>The files are brought back from cold storage. They show as Restoring until they are ready to view and share again.</p>}
        />
      )}
      {dialog?.type === "delete" && (
        <ConfirmModal
          title={`Delete ${plural(selected.size, "asset")}?`}
          confirmLabel="Delete permanently"
          tone="danger"
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await assetApi.deleteAssets([...selected]);
            finish(`Deleted ${plural(selected.size, "asset")}`);
          }}
          body={
            <>
              <p>This permanently removes the files from the platform. It can't be undone.</p>
              {linkTotal > 0 && (
                <p className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-800">
                  {plural(linked.length, "of these asset")} {linked.length === 1 ? "is" : "are"} linked to Events or Products ({plural(linkTotal, "link")} in total). Those
                  links will be removed; the Events and Products themselves stay.
                </p>
              )}
              <ul className="text-slate-500 list-disc pl-5">
                {selectedAssets.slice(0, 5).map((a) => <li key={a.id} className="truncate">{a.filename}</li>)}
                {selectedAssets.length > 5 && <li>and {selectedAssets.length - 5} more</li>}
              </ul>
            </>
          }
        />
      )}
      {dialog?.type === "share" && <ShareModal assets={selectedAssets} onClose={() => setDialog(null)} onDone={finish} />}
      {dialog?.type === "unlink" && <UnlinkModal assets={selectedAssets} onClose={() => setDialog(null)} onDone={finish} />}
      {dialog?.type === "support" && (
        <SupportModal asset={dialog.asset} onClose={() => setDialog(null)} onRaised={(t) => showToast(`Support ticket ${t.ref} raised`)} />
      )}
    </div>
  );
};

export default ManageAssets;